import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@mui/material';

/**
 * AppResponsiveContainer - A responsive grid container component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Grid items to be rendered
 * @param {number} [props.spacing=4] - Spacing between grid items
 * @param {number} [props.xs=12] - Grid columns on extra-small screens
 * @param {number} [props.sm=6] - Grid columns on small screens
 * @param {number} [props.md=3] - Grid columns on medium screens
 * @param {Object} [props.containerProps] - Additional props for the container Grid
 * @param {Object} [props.itemProps] - Additional props for the item Grid
 */
const AppResponsiveContainer = ({
    children,
    spacing = 4,
    xs = 12,
    sm = 6,
    md = 3,
    containerProps = {},
    itemProps = {},
}) => {
    // Convert children to array to support both single and multiple children
    const childrenArray = React.Children.toArray(children);
    const itemRefs = useRef([]);
    itemRefs.current = Array(childrenArray.length)
            .fill(null)
            .map((_, index) => itemRefs.current[index] || React.createRef());

    useEffect(() => {
        const matchWidths = () => {
            const allRefsValid = itemRefs.current.every(ref => ref.current);
;
            if (allRefsValid) {
                const maxWidth = Math.max(
                    ...itemRefs.current
                        .filter(ref => ref.current)
                        .map(ref => ref.current.offsetWidth)
                );

                if (maxWidth > 0) {
                    itemRefs.current.forEach(ref => {
                        ref.current.style.width = `${maxWidth}px`;
                    });
                }
            }
        };

        matchWidths();
        window.addEventListener('resize', matchWidths);
        return () => window.removeEventListener('resize', matchWidths);
    }, [itemRefs]);

    return (
        <Grid container spacing={spacing} justifyContent={"center"} alignItems={"stretch"} {...containerProps}>
            {childrenArray.map((child, index) => (
                <Grid
                    item
                    xs={xs}
                    sm={sm}
                    md={md}
                    key={index}
                    ref={itemRefs.current[index]}
                    {...itemProps}
                >
                    {child}
                </Grid>
            ))}
        </Grid>
    );
};

AppResponsiveContainer.propTypes = {
    children: PropTypes.node.isRequired,
    spacing: PropTypes.number,
    xs: PropTypes.number,
    sm: PropTypes.number,
    md: PropTypes.number,
    containerProps: PropTypes.object,
    itemProps: PropTypes.object,
};

export { AppResponsiveContainer };